import React, { useEffect, useRef, useCallback, useState, } from "react";
import type { JSX } from "react";
import styles from "../chatDrawer.module.css";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import dayjs from 'dayjs';
import {
  ai_text_icon as defaultAiTextIcon,
  copy_img,
  good_response,
  bad_response,
  chat_mic_off_icon,
  micon,
  reply,
  switch_project,
  thumpUp,
  thumpDown,
  // chat_close_icon,
  reply_all,
} from "../../../../assets/images/Images";
import ProjectDropdown from "../../../organisms/ProjectDropdown";
import useChatMessageStore from "../../../../store/chatMessagesStore";
import useChatSessionStore from "../../../../store/chatSessionStore";
import FeedbackForm from "./FeedBack";
import { getDateFromTimeString, formatTimeToAMPM } from "../../../../utils/helper";
import PreprocessAndRenderLatex from "../../../../../../../src/components/ProcessRenderLatex";
import { marked } from "marked";
import { Tooltip } from "react-tooltip";
import type { UploadedFileFromAPI } from "../../../../store/chatMessagesStore";
import type { UploadedFile } from "../../../../store/fileUploadStore";
import { getFileTypeFromName, getFileIcon, getFileTypeLabel } from "../../../atoms/FileTypeUtils/fileTypeUtils";
import { FilePreviewModal } from "../../../molecules/FilePreviewModal";
import type { PreviewFile } from "../../../molecules/FilePreviewModal";
// import EllipsisText from "../../../atoms/Ellipsis";

// Component to display uploaded files in messages (vertical, no remove button) with preview support
// Moved outside MessageList to prevent re-creation on every render
const MessageFileList: React.FC<{ files: UploadedFileFromAPI[] }> = React.memo(({ files }) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewInitialIndex, setPreviewInitialIndex] = useState(0);

  if (!files || files.length === 0) return null;

  // Convert API files to preview files format
  const getPreviewFiles = (): PreviewFile[] => {
    return files
      .filter((file) => file.file_url)
      .map((file) => ({
        url: file.file_url,
        name: file.file_name,
        type: getFileTypeFromName(file.file_name),
      }));
  };

  const handleFileClick = (clickedFile: UploadedFileFromAPI, index: number) => {
    if (!clickedFile.file_url) return;
    
    const previewFiles = getPreviewFiles();
    // Find the index in preview files (in case some files were filtered out)
    const previewIndex = previewFiles.findIndex((f) => f.url === clickedFile.file_url);
    
    if (previewIndex !== -1) {
      setPreviewInitialIndex(previewIndex);
      setIsPreviewOpen(true);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-2 mb-2 max-w-[200px]">
        {files.map((file, index) => {
          const fileType = getFileTypeFromName(file.file_name);
          const hasUrl = !!file.file_url;
          return (
            <div
              key={`${file.file_name}_${index}`}
              className={`flex items-center gap-3 px-2 py-1.5 bg-white border border-gray-300 rounded-md ${
                hasUrl ? "cursor-pointer hover:bg-gray-50 hover:border-gray-400 transition-colors" : ""
              }`}
              onClick={() => handleFileClick(file, index)}
              role={hasUrl ? "button" : undefined}
              tabIndex={hasUrl ? 0 : undefined}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && hasUrl) {
                  e.preventDefault();
                  handleFileClick(file, index);
                }
              }}
            >
              {/* File Icon */}
              <div className="min-w-0">
                {getFileIcon(fileType, "w-6 h-6")}
              </div>

              {/* File Info */}
              <div className="flex-shrink-0 flex flex-col items-start">
                <div className="text-xs font-medium text-gray-900 truncate max-w-[100px]">
                  {file.file_name}
                </div>
                <span className="text-[10px] text-gray-600 mt-0.5">{getFileTypeLabel(file.file_name)}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* File Preview Modal */}
      <FilePreviewModal
        isOpen={isPreviewOpen}
        files={getPreviewFiles()}
        initialIndex={previewInitialIndex}
        onClose={() => setIsPreviewOpen(false)}
      />
    </>
  );
});

interface MessageListProps {
  messages: any[];
  user: any;
  generateInitials: (firstName: string, lastName: string) => string;
  highlightProjectNames: (text: string) => string | (string | JSX.Element)[];
  selectedProject: string | null;
  findProjectNameById: (projectId: string) => string;
  handleCopy: (content: string, id: number) => void;
  handleIconMouseEnter: (
    id: number,
    type: "copy" | "Like" | "speaker" | "switch_project"
  ) => void;
  handleIconMouseLeave: () => void;
  hoveredButton: { id: number; type: string } | null;
  copiedMessageId: number | null;
  handleReply: (
    msg: any,
    projectId: string,
    messageId: string,
    indexId: number
  ) => void;
  handleSpeakerClick: (id: number, content: string) => void;
  handleDropdownToggle: (messageId: number) => void;
  openDropdownMessageId: number | null;
  dropdownRef?: React.RefObject<HTMLDivElement | null>;
  buttonRef?: React.RefObject<HTMLButtonElement | null>;
  showDropdown: boolean;
  setShowDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  projectsList: any;
  agentData?: any[]; // Optional agent data to use instead of fetching
  setSelectedProject: (text: string) => void | null;
  setOpenDropdownMessageId: React.Dispatch<React.SetStateAction<number | null>>;
  isExpanded: boolean;
  chatContainerRef: React.RefObject<HTMLDivElement | null>;
  messagesEndRef?: React.RefObject<HTMLDivElement | null>; // <-- Add this line
  setReplyActive?: (active: boolean) => void;
  aiTextIconUrl?: string;
  isProjectChangeIconVisible?: boolean;
  defaultProjectPlaceholder?: string;
  AIAssistantName?: string;
  // Color customization props for FeedbackForm
  feedbackBackgroundColor?: string;
  feedbackBorderColor?: string;
  feedbackTextColor?: string;
  feedbackButtonColor?: string;
  feedbackButtonHoverColor?: string;
  feedbackPlaceholderColor?: string;
  thumbUpIcon?: string;
  thumbDownIcon?: string;
}

const MessageList: React.FC<MessageListProps> = ({
  messages = [],
  user,
  generateInitials,
  highlightProjectNames,
  selectedProject,
  // findProjectNameById,
  handleCopy,
  handleIconMouseEnter,
  handleIconMouseLeave,
  hoveredButton,
  copiedMessageId,
  handleReply,
  handleSpeakerClick,
  handleDropdownToggle,
  openDropdownMessageId,
  dropdownRef,
  buttonRef,
  showDropdown,
  setShowDropdown,
  projectsList,
  agentData,
  // setSelectedProject,
  setOpenDropdownMessageId,
  isExpanded,
  // chatContainerRef,
  // messagesEndRef,
  // setReplyActive,
  aiTextIconUrl = defaultAiTextIcon,
  isProjectChangeIconVisible = true,
  defaultProjectPlaceholder = "Ask AI",
  AIAssistantName = "Elise AI",
  // Color props for FeedbackForm
  feedbackBackgroundColor,
  feedbackBorderColor,
  feedbackTextColor,
  feedbackButtonColor,
  feedbackButtonHoverColor,
  feedbackPlaceholderColor,
  thumbUpIcon = thumpUp,
  thumbDownIcon = thumpDown,
}) => {
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const [feedbackMsgId, setFeedbackMsgId] = React.useState<string | null>(null);
  const [feedbackText, setFeedbackText] = React.useState("");
  const [showThanksIds, setShowThanksIds] = React.useState<string[]>([]);
  const {
    submitFeedback,
    replyMessage,
    clearAndSetChatMessages,
    setIsWebEnabled,
    isAIResponding,
  } = useChatMessageStore();
  const {
    dropdownSelectedProjectId,
    setDropdownSelectedProjectId,
    setSessionIsOn,
    setSelectedSessionId,
    sessionIsOn,
    setShowNavigationArrows,
    // showNavigationArrows,
  } = useChatSessionStore();
  const feedbackFormRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef(new Map<string, HTMLDivElement>());
  const [highlightedMessageId, setHighlightedMessageId] = useState<
    string | null
  >(null);
  const isInputDisabled = isAIResponding;

  const handleOpenFeedback = (msgId: string) => {
    setFeedbackMsgId(msgId);
    setFeedbackOpen(true);
  };

  const handleCloseFeedback = useCallback(() => {
    setFeedbackOpen(false);
    setFeedbackMsgId(null);
    // Remove the last feedback message from thanks list
    setShowThanksIds((prev) => prev.filter((id) => id !== feedbackMsgId));
  }, [feedbackMsgId]);

  const handleSubmitFeedback = (
    chatMessageId: string,
    comment: string,
    voteType: "like" | "dislike"
  ) => {
    handleCloseFeedback();
    submitFeedback({
      chatMessageId: chatMessageId,
      comment: comment,
      voteType: voteType,
    });
    setFeedbackText("");
    setShowThanksIds((prev) => [...prev, chatMessageId]);
    setTimeout(() => {
      setShowThanksIds((prev) => prev.filter((id) => id !== chatMessageId));
    }, 3000);

    // Do not disturb the scroll position. Avoiding `scrollIntoView` here.
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFeedbackText(e.target.value);
  };
  const thumbUp = (msgId: string) => {
    submitFeedback({
      chatMessageId: msgId.toString(),
      comment: "",
      voteType: "like",
    });
  };

  const thumbDown = (msgId: string, comment: string = "") => {
    submitFeedback({
      chatMessageId: msgId.toString(),
      comment: comment,
      voteType: "dislike",
    });
  };

  const handleProjectDropdownChange = (
    projectId: string,
    projectName: string
  ) => {
    setDropdownSelectedProjectId(projectId);
    setShowNavigationArrows(false);
    setSelectedSessionId(""); // Clear selected session
    setSessionIsOn(false); // Reset session state
    setShowDropdown(false);
    setOpenDropdownMessageId(null);
    if (sessionIsOn) {
      clearAndSetChatMessages();
    }
    setIsWebEnabled(false); // Reset to work mode when changing project
  };
  const scrollToMessage = useCallback((messageId: string) => {
    const targetElement = messageRefs.current.get(messageId);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedMessageId(messageId);
      setTimeout(() => setHighlightedMessageId(null), 1000); // Remove highlight after 1 second
    }
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        feedbackOpen &&
        feedbackFormRef.current &&
        !feedbackFormRef.current.contains(event.target as Node)
      ) {
        handleCloseFeedback();
      }
    }
    if (feedbackOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [feedbackOpen, handleCloseFeedback]);

  // History from the API returns the general agent as "default" / "general - <assistant name>",
  // while live socket messages fall back to defaultProjectPlaceholder. Normalise both so the
  // header + badge look identical before and after a page refresh.
  const isGeneralProject = (projectName?: string) => {
    const name = projectName?.trim().toLowerCase();
    if (!name) return true;
    return (
      name === "default" ||
      name === "general" ||
      name === `general - ${(AIAssistantName ?? "").trim().toLowerCase()}` ||
      name.startsWith("general - ")
    );
  };

  const getProjectLabel = (projectName?: string) =>
    isGeneralProject(projectName)
      ? defaultProjectPlaceholder
      : projectName!.charAt(0).toUpperCase() + projectName!.slice(1);

 // console.warn('messages', messages);


  return (
    <>
      {messages.map((msg, idx) => {
        const prevMsg = idx > 0 ? messages[idx - 1] : null;
        const showProjectDivider =
          prevMsg &&
          (prevMsg.project_id !== msg.project_id ||
            getProjectLabel(prevMsg.project_name) !== getProjectLabel(msg.project_name));

        // Check if date has changed between messages or if it's the first message
        const prevDate = prevMsg ? getDateFromTimeString(prevMsg.time) : null;
        const currentDate = getDateFromTimeString(msg.time);
        const showDateDivider = idx === 0 || (prevDate && currentDate && prevDate !== currentDate);

        // Use a unique key for each message. If _id or id is duplicated, fallback to idx as a suffix.
        const baseKey = msg._id ? msg._id : msg.id ? `${msg.id}` : `idx`;
        // Check for duplicates in the messages array
        const duplicateCount = messages
          .slice(0, idx)
          .filter(
            (m) => (m._id ? m._id : m.id) === (msg._id ? msg._id : msg.id)
          ).length;
        const uniqueKey =
          duplicateCount > 0 ? `${baseKey}-${duplicateCount}` : baseKey;
        const isLastMessage = idx === messages.length - 1;

        // Unique tooltip id per message so each copy button shows its own state
        const copyTooltipId = `copy-btn-${msg._id || msg.id}`;
        // Whether THIS message's copy was the last one triggered
        const isCopied = copiedMessageId === msg.id;

        return (
          <React.Fragment key={uniqueKey}>
            {showDateDivider && (
              <div className="flex justify-center items-center my-4">
                <div className="bg-[#F5F5F5] border border-[#E0E0E0] rounded-[20px] px-4 py-1.5 text-sm text-[#666] font-normal">
                 { dayjs(msg.created_at).format('D MMM')}
                  {/* {dayjs(msg.time).format('DD MMM')} */}
                </div>
              </div>
            )}
            {showProjectDivider && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  margin: "16px 0",
                }}
              >
                <hr
                  style={{
                    flex: 1,
                    border: "none",
                    borderTop: "1px solid #e0e0e0",
                  }}
                />
                <span style={{ margin: "0 12px", color: "#888", fontSize: 12 }}>
                  Switched to course:{" "}
                  {msg.project_name ? getProjectLabel(msg.project_name) : msg.project_id}
                </span>
                <hr
                  style={{
                    flex: 1,
                    border: "none",
                    borderTop: "1px solid #e0e0e0",
                  }}
                />
              </div>
            )}
            {(() => {
              if (msg.reply_message_id && msg.sender === "user") {
                return (
                  <div className="flex justify-start gap-2 mt-4">
                    <div>
                      <img
                        src={reply_all || "/placeholder.svg"}
                        className="w-auto h-auto max-w-[24px] max-h-[24px]"
                        alt="Reply All"
                      />
                    </div>
                    <div
                      onMouseEnter={(e) => {
                        const tooltip =
                          e.currentTarget.querySelector(".reply-tooltip");
                        if (tooltip) {
                          const rect = tooltip.getBoundingClientRect();
                          const spaceBelow = window.innerHeight - rect.bottom;
                          if (spaceBelow < 100) {
                            tooltip.classList.add("show-above");
                            tooltip.classList.remove("show-below");
                          } else {
                            tooltip.classList.add("show-below");
                            tooltip.classList.remove("show-above");
                          }
                        }
                      }}
                      className="cursor-pointer text-xs group relative w-[90%]"
                      onClick={() => scrollToMessage(msg.reply_message_id)}
                    >
                      <div
                        className="font-normal line-clamp-3 text-[#8F8470] block group-hover:line-clamp-none group-hover:whitespace-normal"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "normal",
                        }}
                      >
                        {msg.reply_message ? (
                          typeof msg.reply_message === "string" ? (
                            <PreprocessAndRenderLatex
                              content={marked(msg.reply_message)}
                              isBlockMath={true}
                              wrapperStyle={{}}
                            />
                          ) : (
                            msg.reply_message
                          )
                        ) : (
                          msg.reply_message_id
                        )}
                      </div>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
            <div
              ref={(el) => {
                if (el) {
                  messageRefs.current.set(msg._id || msg.id, el);
                }
              }}
              className={`${msg.sender === "user"
                ? styles["messageRowAI_sender"]
                : styles["messageRowAI"]
                } ${highlightedMessageId === (msg._id || msg.id) &&
                  msg.sender === "ai"
                  ? "ring-2 ring-blue-100"
                  : ""
                }`}
            >
              <div className="flex justify-start gap-2   max-w-full">

                {msg.sender === "user" ? (
                  user?.user_profile_image ? (
                    <img
                      src={user.user_profile_image || "/placeholder.svg"}
                      alt={msg.name}
                      className={"avatar"}
                    />
                  ) : (
                    <div className="flex items-center justify-center w-[30px] h-[30px] bg-[#0068FF] text-white rounded-full">
                      {generateInitials(
                        user?.user_name ?? "",
                        user?.user_last_name ?? ""
                      )}
                    </div>
                  )
                ) : (
                  <img
                    src={
                      aiTextIconUrl || defaultAiTextIcon || "/placeholder.svg"
                    }
                    alt={msg.name}
                    className={`${styles["avatar"]} bg-transparent`}
                  />
                )}
                <div
                  className={`${styles["messageContentWrap"]} ${msg.sender === "user" ? "px-2 py-2 sm:px-6 sm:py-4 bg-profile_border" : "px-0 py-0 bg-none w-[calc(100%-40px)]"} rounded-2xl relative`}
                >
                    {msg.sender === "user" && msg.uploaded_files && msg.uploaded_files.length > 0 && (
                    <MessageFileList files={msg.uploaded_files} />
                  )}
                  {msg.sender === "ai" && (
                    <div className={styles["messageHeader"]}>
                      <span className={styles["senderName"]}>
                        {msg.name}{" "}
                        {msg?.type &&
                          (isGeneralProject(msg.project_name) ||
                            msg.project_name === defaultProjectPlaceholder) &&
                          " - "}{" "}
                        {msg?.type &&
                          (isGeneralProject(msg.project_name) ||
                            msg.project_name === defaultProjectPlaceholder)
                          ? msg.type.charAt(0).toUpperCase() + msg.type.slice(1)
                          : ""}
                      </span>
                      <div className="ml-0 sm:ml-2 px-2 sm:px-4 py-1 rounded-xl text-[10px] sm:text-sm font-semibold text-mid-black bg-chat-dropdown-bg focus:outline-none bg-[#F4F0EB]">
                        {getProjectLabel(msg.project_name)}
                      </div>
                      <div className="hidden sm:block bg-line w-[1.6px] h-[20px] mx-2 rounded-full"></div>
                      <span className={styles["time"]}>{dayjs(msg.created_at).format('h:mm A')}</span>
                    </div>
                  )}
                  <div
                    className={
                      msg.sender === "user"
                        ? styles["messageBubbleUser"]
                        : isExpanded
                          ? styles["messageBubbleAI"]
                          : styles["messageBubbleAI1"]
                    }
                  >
                    {msg.sender === "ai" ? (
                      typeof msg.content === "string" ? (
                        <PreprocessAndRenderLatex
                          content={marked(msg.content)}
                          isBlockMath={true}
                          wrapperStyle={{}}
                        />
                      ) : (
                        msg.content
                      )
                    ) : typeof msg.content === "string" ? (
                      highlightProjectNames(msg.content)
                    ) : (
                      msg.content
                    )}
                  </div>
                  {msg.sender === "user" && (
                    <div className="flex mt-2 justify-end">
                      <div
                        className={`text-[9px] sm:text-sm ${msg.sender === "user" ? "text-gray-500" : "text-gray-700"}`}
                      >
                        {dayjs(msg.created_at).format('h:mm A')} <br></br>
                      </div>
                    </div>
                  )}
                  {/* Speaker, Copy, and Dropdown buttons for AI messages */}
                  {msg.sender === "ai" &&
                    typeof msg.content === "string" &&
                    msg.content && (
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          marginTop: 4,
                          alignItems: "center",
                          position: "relative",
                        }}
                      >
                        {/* Copy button — unique tooltip id per message so each
                            button can independently show "Copied!" vs "Copy" */}
                        <button
                          className="copy-button cursor-pointer"
                          onClick={() => {
                            handleCopy(
                              typeof msg.content === "string"
                                ? msg.content
                                : "",
                              msg.id
                            );
                          }}
                          onMouseLeave={handleIconMouseLeave}
                          style={{ position: "relative", cursor: "pointer" }}
                          data-tooltip-id={copyTooltipId}
                        >
                          <img src={copy_img} alt="Copy to clipboard" />
                          {/* Inline hover label — mirrors the tooltip state */}
                          {hoveredButton?.id === msg.id &&
                            hoveredButton?.type === "copy" && (
                              <span className="text-xs text-white ml-1 absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-black py-1 px-2 rounded-md shadow-lg z-10">
                                {isCopied ? "Copied!" : "Copy"}
                              </span>
                            )}
                        </button>
                        {/* Per-message Tooltip: shows "Copied!" for 5 s after
                            handleCopy fires, then reverts to "Copy" once the
                            parent clears copiedMessageId */}
                        <Tooltip
                          id={copyTooltipId}
                          place="top"
                          content={isCopied ? "Copied!" : "Copy"}
                        />

                        {/* Good response (thumb) button and feedback modal */}
                        <button
                          className="cursor-pointer"
                          onClick={() => {
                            thumbUp(msg._id);
                            setShowThanksIds((prev) => [...prev, msg._id]);
                            setTimeout(() => {
                              setShowThanksIds((prev) =>
                                prev.filter((id) => id !== msg._id)
                              );
                            }, 3000);
                          }}
                          data-tooltip-id="like-button"
                          style={{ cursor: 'pointer' }}
                        >
                          <img
                            src={msg.thumbs_up ? (thumbUpIcon || "/placeholder.svg") : (good_response || "/placeholder.svg")}
                            alt={msg.thumbs_up ? "Liked" : "Like"}
                          />
                        </button>
                        <Tooltip
                          id="like-button"
                          place="top"
                          content="Like"
                        />
                        {msg.thumbs_down ? (
                          <>
                            <button
                              className="focus:outline-none cursor-pointer"
                              onClick={() => {
                                const comment = msg.reaction_description || "";
                                thumbDown(msg._id, comment);
                                setShowThanksIds((prev) => [...prev, msg._id]);
                                setTimeout(() => {
                                  setShowThanksIds((prev) =>
                                    prev.filter((id) => id !== msg._id)
                                  );
                                }, 3000);
                              }}
                              style={{ cursor: 'pointer' }}
                              data-tooltip-id="undo-dislike-button"
                            >
                              <img
                                src={thumbDownIcon || "/placeholder.svg"}
                                alt="good_response"
                              />
                            </button>
                            <Tooltip
                              id="undo-dislike-button"
                              place="top"
                              content="Undo"
                            />
                          </>
                        ) : (
                          <>
                            <button
                              className="cursor-pointer"
                              onClick={() => {
                                handleOpenFeedback(msg._id);
                                setShowThanksIds((prev) => [...prev, msg._id]);
                                setTimeout(() => {
                                  setShowThanksIds((prev) =>
                                    prev.filter((id) => id !== msg._id)
                                  );
                                }, 3000);
                              }}
                              style={{ cursor: 'pointer' }}
                              data-tooltip-id="dislike-button"
                            >
                              <img
                                src={bad_response || "/placeholder.svg"}
                                alt="bad_response"
                              />
                            </button>
                            <Tooltip
                              id="dislike-button"
                              place="top"
                              content="Dislike"
                            />
                          </>
                        )}
                        {/* Feedback modal */}
                        {/* speaker button */}
                        <button
                          className="speakerButton cursor-pointer"
                          onClick={() =>
                            handleSpeakerClick(
                              msg.id,
                              (msg.content as string).replace(/<[^>]+>/g, "")
                            )
                          }
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            cursor: 'pointer'
                          }}
                          aria-label="Read message aloud"
                          onMouseLeave={handleIconMouseLeave}
                          data-tooltip-id="speaker-button"
                        >
                          {msg.isSpeaking ? (
                            <img
                              src={chat_mic_off_icon || "/placeholder.svg"}
                              alt="Turn microphone off"
                            />
                          ) : (
                            <img
                              src={micon || "/placeholder.svg"}
                              alt="Microphone on"
                            />
                          )}
                          {hoveredButton?.id === msg.id &&
                            hoveredButton?.type === "speaker" && (
                              <span className="text-xs text-white ml-1 absolute bottom-6 left-7 transform translate-x-1/2 bg-black py-1 px-2 rounded-md shadow-lg z-10">
                                Read aloud
                              </span>
                            )}
                        </button>
                        <Tooltip
                          id="speaker-button"
                          place="top"
                          content="Speaker"
                        />
                        {/* Reply Icon */}
                        <button
                          className="cursor-pointer"
                          onClick={() => {
                            handleReply(
                              msg,
                              msg.project_id,
                              msg._id,
                              msg.index_id
                            );
                          }}
                          style={{ cursor: 'pointer' }}
                          data-tooltip-id="Reply-button"
                        >
                          <img src={reply} alt="" />
                        </button>
                        <Tooltip
                          id="Reply-button"
                          place="top"
                          content="Reply"
                        />
                        {/* Switch icon */}
                        {isProjectChangeIconVisible && (
                          <>
                            <button
                              id="ai-project-dropdown-toggle-button"
                              onClick={() => {
                                handleDropdownToggle(msg.id);
                              }}
                              aria-label="Show projects dropdown"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                                cursor: 'pointer'
                              }}
                              ref={buttonRef as React.RefObject<HTMLButtonElement>}
                              data-tooltip-id="Switch-button"
                              onMouseLeave={handleIconMouseLeave}
                              className={`${styles["speakerButton"]} cursor-pointer ${replyMessage || isInputDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                              disabled={
                                replyMessage || isInputDisabled ? true : false
                              }
                            >
                              <img
                                src={switch_project || "/placeholder.svg"}
                                alt="Switch Project"
                              />
                              {hoveredButton?.id === msg.id &&
                                hoveredButton?.type === "switch_project" &&
                                !replyMessage && (
                                  <span className="text-xs text-white left-4 bottom-6 absolute transform translate-x-1/2 bg-black py-1 px-2 rounded-md shadow-lg z-10">
                                    Switch course (General - {AIAssistantName})
                                  </span>
                                )}
                            </button>
                            <Tooltip
                              id="Switch-button"
                              place="top"
                              content="Switch"
                            />
                          </>
                        )}
                        {openDropdownMessageId === msg.id && (
                          <div
                            ref={dropdownRef as React.RefObject<HTMLDivElement>}
                            className="absolute mt-2 w-full z-50"
                          >
                            <ProjectDropdown
                              AIAssistantName={AIAssistantName}
                              onChange={handleProjectDropdownChange}
                              initialProjectId={dropdownSelectedProjectId ?? undefined}
                              showDropdown={showDropdown}
                              setShowDropdown={setShowDropdown}
                              agentData={agentData}
                              isLastMessage={isLastMessage}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  {feedbackOpen &&
                    feedbackMsgId === msg?._id &&
                    msg?.sender === "ai" &&
                    isLastMessage ? (
                    <div className="relative">
                      <div
                        ref={feedbackFormRef}
                        className="absolute bottom-8 right-0 w-full z-50"
                      >
                        <FeedbackForm
                          feedbackText={feedbackText}
                          onFeedbackChange={handleInputChange}
                          onSubmit={() =>
                            handleSubmitFeedback(
                              msg?._id,
                              feedbackText,
                              "dislike"
                            )
                          }
                          onClose={handleCloseFeedback}
                          backgroundColor={feedbackBackgroundColor}
                          borderColor={feedbackBorderColor}
                          textColor={feedbackTextColor}
                          buttonColor={feedbackButtonColor}
                          buttonHoverColor={feedbackButtonHoverColor}
                          placeholderColor={feedbackPlaceholderColor}
                        />
                      </div>
                    </div>
                  ) : (
                    feedbackOpen &&
                    feedbackMsgId === msg?._id &&
                    msg?.sender === "ai" && (
                      <div ref={feedbackFormRef}>
                        <FeedbackForm
                          feedbackText={feedbackText}
                          onFeedbackChange={handleInputChange}
                          onSubmit={() =>
                            handleSubmitFeedback(
                              msg?._id,
                              feedbackText,
                              "dislike"
                            )
                          }
                          onClose={handleCloseFeedback}
                          backgroundColor={feedbackBackgroundColor}
                          borderColor={feedbackBorderColor}
                          textColor={feedbackTextColor}
                          buttonColor={feedbackButtonColor}
                          buttonHoverColor={feedbackButtonHoverColor}
                          placeholderColor={feedbackPlaceholderColor}
                        />
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </React.Fragment >
        );
      })}
      <style>
        {`
        .reply-tooltip.show-below {
          top: 100%;
        }
        .reply-tooltip.show-above {
          bottom: 100%;
          top: auto;
          margin-bottom: 8px;
        }
        `}
      </style>
    </>
  );
};

export default MessageList;